import {
  generateOAuthUrl,
  type OAuthAccessLevel,
} from "@newform-monorepo/backend/src/lib/oauth/oauth-scopes";
import type { AccessCapability } from "@newform-monorepo/backend/src/lib/services/platformAccess/registry";
import type { Platform } from "@newform-monorepo/db";
import { clickNonceCookieName, issueClickNonce } from "@/lib/oauthClickNonce";
import { isPlatformName, PLATFORM_METADATA } from "@/lib/platformRegistry";

/** The ad account row an OAuth round trip was for, on the page it returns to. */
export const OAUTH_ACCOUNT_PARAM = "adAccountId";
/** Why the round trip didn't complete (an {@link OAuthErrorReason}). */
export const OAUTH_ERROR_PARAM = "connectError";
/** Which platform the round trip was with, so the message can name it. */
export const OAUTH_PLATFORM_PARAM = "connectPlatform";

/**
 * Holds the nonce a connect click put in the OAuth `state`. The callback
 * stores a token only when the two match, so a consent link someone else
 * built, naming their own organization, can't land a token from this
 * browser.
 */
export const OAUTH_NONCE_COOKIE = clickNonceCookieName("nf_platform_oauth");

/** Long enough to get through a consent screen. */
const OAUTH_NONCE_MAX_AGE_SECONDS = 60 * 60;

const OAUTH_ERROR_MESSAGES = {
  cancelled: (name: string) =>
    `${name} wasn't connected, so nothing changed. Reconnect when you're ready.`,
  failed: (name: string) =>
    `Couldn't save the ${name} connection. Try reconnecting, and contact support if it keeps failing.`,
  unverified: (name: string) =>
    `${name} wasn't connected: this sign-in didn't come from the latest Connect in this browser. Connect again from this page.`,
};

export type OAuthErrorReason = keyof typeof OAUTH_ERROR_MESSAGES;

function isOAuthErrorReason(value: string): value is OAuthErrorReason {
  return Object.hasOwn(OAUTH_ERROR_MESSAGES, value);
}

/**
 * The message for a round trip that didn't complete. The URL carries only a
 * reason code and a platform, never the text: anyone can craft a link, and a
 * toast should only ever say something we wrote. Null for anything else.
 */
export function oauthErrorMessage(
  reason: string | null,
  platform: string | null,
): string | null {
  if (!reason || !platform || !isOAuthErrorReason(reason)) return null;
  if (!isPlatformName(platform)) return null;
  return OAUTH_ERROR_MESSAGES[reason](PLATFORM_METADATA[platform].name);
}

/**
 * Where an OAuth round trip comes back to: the page it started on, with the
 * account it was for, so that page can reopen it. A leftover error from an
 * earlier attempt is dropped.
 */
export function oauthReturnPath(
  pathname: string,
  search: string | URLSearchParams | null,
  adAccountId: string,
): string {
  const params = new URLSearchParams(search ?? undefined);
  params.set(OAUTH_ACCOUNT_PARAM, adAccountId);
  params.delete(OAUTH_ERROR_PARAM);
  params.delete(OAUTH_PLATFORM_PARAM);
  return `${pathname}?${params.toString()}`;
}

/**
 * Sends the browser to the platform's consent screen for an ad account row.
 * Call it from the click itself: it records a fresh nonce for the callback to
 * check (see {@link OAUTH_NONCE_COOKIE}), so a URL built ahead of time would
 * carry a stale one. False when the browser blocked a new tab.
 */
export function startPlatformConnect(
  platform: Platform,
  slug: string,
  /** An in-app path from {@link oauthReturnPath}. */
  returnTo: string,
  accountId: string,
  accessLevel?: OAuthAccessLevel,
  options: {
    /** Capabilities the reconnect exists to add, on top of the baseline. */
    capabilities?: readonly AccessCapability[];
    /** Ask again for permissions the user declined before (Meta). */
    rerequest?: boolean;
    /** Open the consent screen in a new tab and leave this page as it is. */
    newTab?: boolean;
  } = {},
): boolean {
  // AppLovin authenticates with a report key pasted into the account form, so
  // there is no authorization URL to send anyone to.
  if (!isPlatformName(platform)) {
    throw new Error(`No connect URL for platform ${platform}`);
  }
  const url = generateOAuthUrl(platform, {
    slug,
    returnTo,
    accountId,
    // Lax: the platform sends the browser back to the callback from its own
    // site, and a Strict cookie would stay behind.
    nonce: issueClickNonce(OAUTH_NONCE_COOKIE, {
      maxAgeSeconds: OAUTH_NONCE_MAX_AGE_SECONDS,
      sameSite: "Lax",
    }),
    accessLevel,
    capabilities: options.capabilities,
    rerequest: options.rerequest,
  });
  if (options.newTab) {
    // "noopener" would make window.open return null even when it works, so
    // the opener is cut by hand. A blocked tab reports false and leaves this
    // page, and any unsaved form on it, as it is.
    const opened = window.open(url, "_blank");
    if (!opened) return false;
    opened.opener = null;
    return true;
  }
  window.location.assign(url);
  return true;
}
